import { create } from "zustand";

export interface UpdateState {
  updateAvailable: boolean;
  updateReady: boolean;
  version: string | null;
  hash: string | null;
  status: string;
  message: string;
  progress: number;
  bytesDownloaded: number;
  totalBytes: number;
  error: string | null;
  checking: boolean;
  downloading: boolean;
  setChecking: (checking: boolean) => void;
  setDownloading: (downloading: boolean) => void;
  setUpdateInfo: (info: {
    updateAvailable: boolean;
    updateReady: boolean;
    version: string;
    hash: string;
    error: string;
  }) => void;
  setStatus: (
    status: string,
    message: string,
    details?: { progress?: number; bytesDownloaded?: number; totalBytes?: number; error?: string },
  ) => void;
  reset: () => void;
}

export const useUpdateStore = create<UpdateState>((set) => ({
  updateAvailable: false,
  updateReady: false,
  version: null,
  hash: null,
  status: "idle",
  message: "",
  progress: 0,
  bytesDownloaded: 0,
  totalBytes: 0,
  error: null,
  checking: false,
  downloading: false,
  setChecking: (checking) => set({ checking }),
  setDownloading: (downloading) => set({ downloading }),
  setUpdateInfo: (info) =>
    set({
      updateAvailable: info.updateAvailable,
      updateReady: info.updateReady,
      version: info.version,
      hash: info.hash,
      error: info.error || null,
    }),
  setStatus: (status, message, details) =>
    set((state) => {
      const isComplete = status === "download-complete";
      const isError = status === "error" || status === "patch-failed";
      return {
        status,
        message,
        progress: details?.progress ?? state.progress,
        bytesDownloaded: details?.bytesDownloaded ?? state.bytesDownloaded,
        totalBytes: details?.totalBytes ?? state.totalBytes,
        error: details?.error || (isError ? message : null),
        updateReady: isComplete ? true : state.updateReady,
        downloading: isComplete || isError ? false : state.downloading,
      };
    }),
  reset: () =>
    set({
      updateAvailable: false,
      updateReady: false,
      version: null,
      hash: null,
      status: "idle",
      message: "",
      progress: 0,
      bytesDownloaded: 0,
      totalBytes: 0,
      error: null,
      checking: false,
      downloading: false,
    }),
}));
